/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable react-hooks/exhaustive-deps */
import type { User } from "oidc-client-ts";
import type { FC, ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import {
  AuthProvider as OIDCAuthProvider,
  hasAuthParams,
  useAuth,
} from "react-oidc-context";

import { ladsWebSocketService } from "../util/websocket-lads";
import { oidcConfig } from "./oidc-config";

// Runtime-substituted via env.sh (one image, configurable per deployment). Route the
// build-inlined value through a runtime call so esbuild doesn't constant-fold the
// `"_APP_OAUTH_DISABLED" === "true"` comparison away — that would strip the placeholder.
const OAUTH_DISABLED =
  `${import.meta.env.VITE_OAUTH_DISABLED}`.trim().toLowerCase() === "true";
console.warn(
  `OAUTH_DISABLED:${OAUTH_DISABLED} -> derived from: ${
    import.meta.env.VITE_OAUTH_DISABLED
  }`
);
// User info from OIDC token
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  groups: string[];
}

// Mock user for when OAuth is disabled
const ANONYMOUS: AuthUser = {
  id: "anonymous",
  email: "anonymous@lcc.waldner.de",
  name: "Anonymous",
  groups: ["admin"],
};

// Context for authentication state
interface AuthContextType {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => {},
  logout: () => {},
});

// Parse OIDC user into our AuthUser format
function parseUser(oidcUser: User | null | undefined): AuthUser | null {
  if (!oidcUser?.profile) return null;

  // Debug: log the full profile to see what Authentik sends
  console.log("[Auth] User profile from OIDC:", oidcUser.profile);

  return {
    id: oidcUser.profile.sub,
    email: oidcUser.profile.email || "",
    name:
      oidcUser.profile.name ||
      oidcUser.profile.preferred_username ||
      oidcUser.profile.email ||
      "",
    groups: (oidcUser.profile.groups as string[]) || [],
  };
}

// Inner component that uses OIDC hooks
const AuthProviderInner: FC<{
  children: ReactNode;
  LoadingScreen: React.ComponentType<any>;
  ErrorScreen: React.ComponentType<any>;
}> = ({ ErrorScreen, LoadingScreen, children }) => {
  const auth = useAuth();
  const [hasTriedSignin, setHasTriedSignin] = useState(false);

  // Check if we're on the callback page or have auth params in URL
  const hasParams = hasAuthParams();

  // Debug logging
  console.log("[Auth Debug]", {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    hasParams,
    hasTriedSignin,
    activeNavigator: auth.activeNavigator,
    user: auth.user ? "present" : "null",
    accessToken: auth.user?.access_token
      ? "present (" + auth.user.access_token.slice(0, 20) + "...)"
      : "null",
    error: auth.error?.message,
  });

  // Auto-login: redirect to Authentik if not authenticated and not processing callback
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Don't redirect if:
    // - Already authenticated
    // - Currently loading/processing
    // - Have auth params (callback in progress)
    // - Already tried to sign in
    // - Active navigator (redirect in progress)
    if (
      auth.isAuthenticated ||
      auth.isLoading ||
      hasParams ||
      hasTriedSignin ||
      auth.activeNavigator
    ) {
      return;
    }

    setHasTriedSignin(true);
    auth.signinRedirect();
  }, [
    auth.isAuthenticated,
    auth.isLoading,
    hasParams,
    hasTriedSignin,
    auth.activeNavigator,
  ]);

  // Keep the LADS WebSocket handshake token in sync with the access token.
  // Runs before the early returns below so the hook order stays stable.
  useEffect(() => {
    ladsWebSocketService.setToken(auth.user?.access_token ?? null);
  }, [auth.user?.access_token]);

  // Handle callback errors
  if (auth.error) {
    console.error("Auth error:", auth.error);
    return <ErrorScreen error={auth.error} />;
  }

  // CRITICAL: Never render children until fully authenticated
  // Show loading while:
  // - OIDC is loading
  // - Processing callback (has auth params but not yet authenticated)
  // - Not authenticated (will redirect to login)
  if (auth.isLoading || !auth.isAuthenticated) {
    return <LoadingScreen />;
  }

  const contextValue: AuthContextType = {
    user: parseUser(auth.user),
    accessToken: auth.user?.access_token || null,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    login: () => auth.signinRedirect(),
    logout: () => auth.signoutRedirect(),
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Provider for when OAuth is disabled - renders children directly with mock auth
const DisabledAuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const contextValue: AuthContextType = {
    user: ANONYMOUS,
    // do not send access token. Backend needs to run with OIDC_DISABLED=true!
    accessToken: null,
    isAuthenticated: true,
    isLoading: false,
    login: () => console.log("OAuth disabled - login not available"),
    logout: () => console.log("OAuth disabled - logout not available"),
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Main AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
  LoadingScreen: React.ComponentType<any>;
  ErrorScreen: React.ComponentType<any>;
}

export const AuthProvider: FC<AuthProviderProps> = ({
  ErrorScreen,
  LoadingScreen,
  children,
}) => {
  // If OAuth is disabled, skip OIDC entirely
  if (OAUTH_DISABLED) {
    return <DisabledAuthProvider>{children}</DisabledAuthProvider>;
  }

  return (
    <OIDCAuthProvider {...oidcConfig}>
      <AuthProviderInner
        ErrorScreen={ErrorScreen}
        LoadingScreen={LoadingScreen}
      >
        {children}
      </AuthProviderInner>
    </OIDCAuthProvider>
  );
};

// Hook to access authentication state
export const useAuthContext = () => useContext(AuthContext);

// Hook to get just the access token (for API calls)
// Returns null if not authenticated (so SWR won't fetch)
export const useAccessToken = () => {
  const { accessToken } = useContext(AuthContext);
  // console.log("[useAccessToken]", accessToken ? "present (" + accessToken.substring(0, 20) + "...)" : "null");
  return accessToken || null;
};

// Hook to get the current user
export const useCurrentUser = () => {
  const { user } = useContext(AuthContext);
  return user;
};
